import { useMemo, useState } from 'react';
import { Button, Dialog, Field, formatMoney } from '@elysian/ui';
import type { CashDocument, DocumentSummary } from '../../data/source';
import { useAsync } from '../../data/useAsync';
import { useSession } from '../../state/session';
import { iso, isAmount, shortDate } from '../invoices/shared';

/** What the toast says once a receipt is recorded. */
export const receiptToast = (r: CashDocument) => (r.type === 'payment'
  ? `${r.number}: ${formatMoney(r.amount)} to ${r.contact_name} from ${r.bank_account?.name ?? 'the bank'}`
  : `${r.number}: ${formatMoney(r.amount)} from ${r.contact_name} into ${r.bank_account?.name ?? 'the bank'}`)
  + (Number(r.on_account) > 0 ? `; ${formatMoney(r.on_account)} on account` : '');

const cents = (v: string) => Math.round(Number(v) * 100);
const euro = (c: number) => (c / 100).toFixed(2);

/** Oldest first, until the money runs out (#26). */
function suggest(invoices: DocumentSummary[], amount: number, first?: string): Record<string, string> {
  let left = amount;
  const out: Record<string, string> = {};
  const order = first ? [...invoices.filter((d) => d.id === first), ...invoices.filter((d) => d.id !== first)] : invoices;
  for (const d of order) {
    const take = Math.max(0, Math.min(left, cents(d.outstanding)));
    out[d.id] = take > 0 ? euro(take) : '';
    left -= take;
  }
  return out;
}

/**
 * Record money received from a customer, or paid to a supplier, by hand (#26):
 * when, how much, which bank account it went into (out of) (that's how it was
 * paid), and which invoices (bills) it pays, oldest first unless changed.
 * Anything not allocated stays on their account as credit.
 */
export function RecordReceipt({ role = 'customer', contactId, contactName, invoiceId, onClose, onDone }: {
  /** customer: a receipt against invoices; supplier: a payment against bills. */
  role?: 'customer' | 'supplier';
  contactId: string;
  contactName: string;
  /** Opened from an invoice (bill): that one first, and its amount to start with. */
  invoiceId?: string;
  onClose: () => void;
  onDone: (r: CashDocument) => void;
}) {
  const { source, today } = useSession();
  const sales = role === 'customer';
  const W = sales
    ? { title: 'Record a receipt from', date: 'Date received', amount: 'Amount received (€)', bank: 'Paid into', bankHint: 'The bank account it went into: that’s how it was paid',
        ref: 'Cheque number, their remittance, etc.', docs: 'invoices', doc: 'Invoice', button: 'Record receipt', none: 'has no unpaid invoices' }
    : { title: 'Record a payment to', date: 'Date paid', amount: 'Amount paid (€)', bank: 'Paid from', bankHint: 'The bank account it went out of: that’s how it was paid',
        ref: 'Cheque number, transfer reference, etc.', docs: 'bills', doc: 'Bill', button: 'Record payment', none: 'has no unpaid bills' };
  const data = useAsync(async () => {
    const [docs, banks] = await Promise.all([source.documents({ type: sales ? 'sales_invoice' : 'purchase_bill', contactId }), source.bankAccounts()]);
    const open = docs.filter((d) => (d.status === 'posted' || d.status === 'part_paid') && Number(d.outstanding) > 0 && d.currency === 'EUR')
      .sort((a, b) => a.issue_date.localeCompare(b.issue_date) || (a.doc_no ?? '').localeCompare(b.doc_no ?? ''));
    return { open, banks: banks.filter((b) => b.is_active !== false && b.currency === 'EUR') };
  }, [source, contactId, sales]);
  const [date, setDate] = useState(iso(today()));
  const [amount, setAmount] = useState<string | null>(null);
  const [bank, setBank] = useState<string | null>(null);
  const [reference, setReference] = useState('');
  const [alloc, setAlloc] = useState<Record<string, string> | null>(null);
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const open = data.state === 'ready' ? data.data.open : [];
  const banks = data.state === 'ready' ? data.data.banks : [];
  // Starting values once loaded: the invoice's amount (from an invoice), the default bank account.
  if (data.state === 'ready' && amount === null) {
    const start = invoiceId ? open.find((d) => d.id === invoiceId)?.outstanding : undefined;
    setAmount(start ? Number(start).toFixed(2) : '');
    setBank(banks.find((b) => b.details?.is_default)?.code ?? banks[0]?.code ?? '');
    setAlloc(suggest(open, start ? cents(start) : 0, invoiceId));
  }

  const amountOk = !!amount && isAmount(amount) && Number(amount) > 0;
  const total = useMemo(() => Object.values(alloc ?? {}).reduce((t, v) => t + (v.trim() && isAmount(v) ? cents(v) : 0), 0), [alloc]);
  const got = amountOk ? cents(amount!) : 0;
  const onAccount = got - total;
  const tooMuch = open.find((d) => alloc?.[d.id] && isAmount(alloc[d.id]!) && cents(alloc[d.id]!) > cents(d.outstanding));
  const problem = !amountOk ? null : onAccount < 0 ? `That’s ${formatMoney(euro(-onAccount))} more than was received`
    : tooMuch ? `${tooMuch.doc_no} has only ${formatMoney(tooMuch.outstanding)} left to pay` : null;

  function changeAmount(v: string) {
    setAmount(v);
    // Until someone types an allocation, keep suggesting: oldest first.
    if (!touched && isAmount(v)) setAlloc(suggest(open, cents(v), invoiceId));
  }

  async function save() {
    setBusy(true);
    setError(null);
    try {
      const allocations = Object.entries(alloc ?? {}).filter(([, v]) => v.trim() && isAmount(v) && Number(v) > 0)
        .map(([document_id, v]) => ({ document_id, amount: Number(v).toFixed(2) }));
      onDone(await (sales ? source.recordReceipt : source.recordPayment)({ contact_id: contactId, date, amount: Number(amount).toFixed(2), bank_account: bank!,
        reference: reference.trim() || null, allocations }));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  }

  return (
    <Dialog title={`${W.title} ${contactName}`} onClose={onClose}
      actions={<>
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
        <Button variant="primary" disabled={busy || !amountOk || !bank || !!problem || !date} onClick={() => void save()}>
          {busy ? 'Saving…' : W.button}
        </Button>
      </>}>
      {data.state === 'loading' && <div className="el-skeleton" style={{ height: 120 }} />}
      {data.state === 'error' && <div className="el-error-box" role="alert">{data.error.message}</div>}
      {data.state === 'ready' && (
        <>
          <div className="el-form-grid">
            <Field id="rc-date" label={W.date}>
              <input id="rc-date" type="date" className="el-input" value={date} onChange={(e) => setDate(e.target.value)} />
            </Field>
            <Field id="rc-amount" label={W.amount} error={amount && !isAmount(amount) ? 'An amount, such as 250.00' : null}>
              <input id="rc-amount" className="el-input el-num" inputMode="decimal" value={amount ?? ''} autoFocus={!invoiceId}
                onChange={(e) => changeAmount(e.target.value)} />
            </Field>
            <Field id="rc-bank" label={W.bank} hint={W.bankHint}>
              <select id="rc-bank" className="el-input" value={bank ?? ''} onChange={(e) => setBank(e.target.value)}>
                {banks.map((b) => <option key={b.code} value={b.code}>{b.name}</option>)}
              </select>
            </Field>
            <Field id="rc-ref" label="Reference (optional)" hint={W.ref}>
              <input id="rc-ref" className="el-input" value={reference} onChange={(e) => setReference(e.target.value)} />
            </Field>
          </div>
          <h3 className="el-label" style={{ margin: '6px 0 0', fontSize: 14 }}>What it pays</h3>
          {open.length === 0 && <p className="el-muted" style={{ margin: 0 }}>{contactName} {W.none}. It all goes on their account as credit.</p>}
          {open.length > 0 && (
            <div className="el-table-wrap">
              <table className="el-table" aria-label={sales ? 'Invoices it pays' : 'Bills it pays'}>
                <thead><tr><th scope="col">{W.doc}</th><th scope="col" className="el-hide-narrow">Dated</th><th scope="col" className="el-num">Unpaid</th><th scope="col" className="el-num">Pay</th></tr></thead>
                <tbody>
                  {open.map((d) => (
                    <tr key={d.id}>
                      <td style={{ whiteSpace: 'nowrap' }}>{d.doc_no}{!sales && d.supplier_ref && <div className="el-hint">{d.supplier_ref}</div>}</td>
                      <td className="el-hide-narrow">{shortDate(d.issue_date)}</td>
                      <td className="el-num">{formatMoney(d.outstanding)}</td>
                      <td className="el-num" style={{ width: 120, minWidth: 100 }}>
                        <input className="el-input el-num" inputMode="decimal" aria-label={`Pay ${d.doc_no}`} value={alloc?.[d.id] ?? ''}
                          onChange={(e) => { setTouched(true); setAlloc({ ...alloc, [d.id]: e.target.value }); }} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {amountOk && (
            <p className="el-receipt-sum" aria-live="polite">
              {formatMoney(euro(total))} against {W.docs}
              {onAccount > 0 && <> · <strong>{formatMoney(euro(onAccount))} on account</strong> as credit to use later</>}
            </p>
          )}
          {problem && <div className="el-error-box" role="alert">{problem}</div>}
          {error && <div className="el-error-box" role="alert">{error}</div>}
        </>
      )}
    </Dialog>
  );
}

/** Use money held on account against the customer's invoices, oldest first (#26). */
export function UseOnAccount({ receipt, onClose, onDone }: { receipt: CashDocument; onClose: () => void; onDone: (msg: string) => void }) {
  const { source } = useSession();
  const invoices = useAsync(async () => (await source.documents({ type: receipt.type === 'payment' ? 'purchase_bill' : 'sales_invoice', contactId: receipt.contact_id }))
    .filter((d) => (d.status === 'posted' || d.status === 'part_paid') && Number(d.outstanding) > 0)
    .sort((a, b) => a.issue_date.localeCompare(b.issue_date)), [source, receipt.contact_id]);
  const [alloc, setAlloc] = useState<Record<string, string> | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const list = invoices.state === 'ready' ? invoices.data : [];
  if (invoices.state === 'ready' && alloc === null) setAlloc(suggest(list, cents(receipt.on_account)));
  const chosen = Object.entries(alloc ?? {}).filter(([, v]) => v.trim() && isAmount(v) && Number(v) > 0);
  const total = chosen.reduce((t, [, v]) => t + cents(v), 0);
  const over = total > cents(receipt.on_account);

  async function go() {
    setBusy(true);
    setError(null);
    try {
      await source.allocateCredit(receipt.id, chosen.map(([document_id, v]) => ({ document_id, amount: Number(v).toFixed(2) })));
      const one = receipt.type === 'payment' ? 'bill' : 'invoice';
      onDone(`${formatMoney(euro(total))} of ${receipt.number} used against ${chosen.length} ${one}${chosen.length === 1 ? '' : 's'}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  }

  return (
    <Dialog title={`Use ${receipt.number} against ${receipt.type === 'payment' ? 'bills' : 'invoices'}`} onClose={onClose}
      actions={<><Button variant="ghost" onClick={onClose}>Cancel</Button>
        <Button variant="primary" disabled={busy || !chosen.length || over} onClick={() => void go()}>Use {formatMoney(euro(total))}</Button></>}>
      <p style={{ margin: 0 }}>{formatMoney(receipt.on_account)} of this {receipt.type === 'payment' ? 'payment' : 'receipt'} is on account. No money moves; the {receipt.type === 'payment' ? 'bills’' : 'invoices’'} balances go down.</p>
      {invoices.state === 'loading' && <div className="el-skeleton" style={{ height: 60 }} />}
      {invoices.state === 'ready' && list.length === 0 && <p className="el-muted" style={{ margin: 0 }}>No unpaid {receipt.type === 'payment' ? 'bills' : 'invoices'}. The credit stays on their account.</p>}
      {list.map((d) => (
        <Field key={d.id} id={`oa-${d.id}`} label={`${d.doc_no} · ${shortDate(d.issue_date)} · ${formatMoney(d.outstanding)} unpaid`}>
          <input id={`oa-${d.id}`} className="el-input el-num" inputMode="decimal" value={alloc?.[d.id] ?? ''}
            onChange={(e) => setAlloc({ ...alloc, [d.id]: e.target.value })} />
        </Field>
      ))}
      {over && <div className="el-error-box" role="alert">That’s more than is on account.</div>}
      {error && <div className="el-error-box" role="alert">{error}</div>}
    </Dialog>
  );
}
